/* eslint-disable one-var */
import moment from 'moment';
import { validFromInitialValue, validToInitialValue } from '../settings/constants/dates';
import globals from '../globals/communityGlobals';

export const parseDateUtc = date => moment(date).utcOffset(0, true);
const TODAY = globals.now;

export const startEndTimeDisplay = (startDate, endDate) => {
  /* splitting startDate, endDate by 'T' to separate the Date and Time values as only Time is needed
  yyyy-MM-ddTHH:mm:ss is the format of the DateTime objects we get, that is why there is split by 'T' */
  const startTime = startDate.split('T');
  const endTime = endDate.split('T');
  return `${moment(startTime[1], 'HH:mm:ss').format('HH:mm')} - ${moment(endTime[1], 'HH:mm:ss').format('HH:mm')}`;
};

export const startTimeDisplay = startDate => {
  const startTime = startDate.split('T');
  return `${moment(startTime[1], 'HH:mm:ss').format('HH:mm')}`;
};

export const getDateForApi = date =>
  date
    ? moment(date)
        .utcOffset(0, true)
        .format(globals.dateFormats.localApiFormat)
    : null;

// eslint-disable-next-line no-confusing-arrow
export const addDateTimeMinutes = (date, minutes) =>
  date
    ? moment(date)
        .utcOffset(0, true)
        .add(minutes, 'minutes')
        .format(globals.dateFormats.localApiFormat)
    : null;

export const clearBorderValues = date => {
  const dateString = getDateForApi(date);
  return dateString === validFromInitialValue || dateString === validToInitialValue ? null : date;
};

export const durationBetweenDates = (start, end) => {
  end.setDate(start.getDate());
  if (end < start) end.setDate(end.getDate() + 1);
  return moment(end)
    .startOf('minute')
    .diff(moment(start).startOf('minute'), 'minutes');
};

export const isBetweenDateRange = (dateRange, compareToDateRange) => {
  const startDateIsValid = moment(parseDateUtc(dateRange.startDate)).isBetween(
    moment(parseDateUtc(compareToDateRange.startDate)),
    moment(parseDateUtc(compareToDateRange.endDate)),
    null,
    '[]',
  );

  const endDateIsValid = moment(parseDateUtc(dateRange.endDate)).isBetween(
    moment(parseDateUtc(compareToDateRange.startDate)),
    moment(parseDateUtc(compareToDateRange.endDate)),
    null,
    '[]',
  );

  return startDateIsValid && endDateIsValid;
};

export function isBetweenFromAndToDates({ dateFrom, dateTo }, value) {
  if (typeof value === 'undefined' || typeof dateFrom === 'undefined') return true;
  const isDateToSet = typeof dateTo !== 'undefined';
  const momentFrom = moment(dateFrom).startOf('day');
  if (isDateToSet) {
    return moment(value)
      .startOf('day')
      .isBetween(momentFrom, moment(dateTo).startOf('day'), null, '[]');
  }
  return moment(value)
    .startOf('day')
    .isSameOrAfter(momentFrom);
}

export function timeIsBetweenDateRange({ dateFrom, dateTo }, value) {
  if (typeof value === 'undefined' || typeof dateFrom === 'undefined' || typeof dateTo === 'undefined') return false;
  const format = 'YYYY-MM-DDTHH:mm:ss';
  const time = moment(value, format).utcOffset(0, true),
    beforeTime = moment(dateFrom, format).utcOffset(0, true),
    afterTime = moment(dateTo, format).utcOffset(0, true);

  return time.isBetween(beforeTime, afterTime, null, '[]');
}

export const mergeDateWithTime = (dateString, timeString) => {
  const dateAt = moment(dateString);
  const timeAt = moment(timeString);

  return dateAt.set('hours', timeAt.get('hours')).set('minutes', timeAt.get('minutes'));
};

export const convertDateFromServer = date => (date ? moment(date).format(globals.dateFormats.localApiFormat) : null);

export function getSchedulerModeTimes(
  selectedDate,
  isNightMode,
  schedulerDayModeStartTime,
  schedulerNightModeStartTime,
) {
  const startHour = isNightMode ? schedulerNightModeStartTime : schedulerDayModeStartTime;
  const endHour = isNightMode ? 24 + parseInt(schedulerDayModeStartTime, 10) : schedulerNightModeStartTime;

  const startTime = parseDateUtc(selectedDate)
    .set({ hour: startHour })
    .format(globals.dateFormats.localApiFormat);
  const endTime = parseDateUtc(selectedDate)
    .set({ hour: endHour })
    .format(globals.dateFormats.localApiFormat);

  return { schedulerStartTime: startTime, schedulerEndTime: endTime };
}

export const parseShortDateTime = date => (date ? parseDateUtc(date).format(globals.dateFormats.short) : '-');

export const parseShortDateWithTime = date =>
  date ? parseDateUtc(date).format(globals.dateFormats.shortWithTime) : '-';

export const parseShortDateWithLongTime = date =>
  date ? parseDateUtc(date).format(globals.dateFormats.shortWithLongTime) : '-';

export const isTodaysDate = date => parseDateUtc(date).isSame(TODAY, 'day');

export const parseDateToLongTime = date => parseDateUtc(date).format('HH:mm:ss.SSS');

export const startEndTimeGridDisplay = (startDate, endDate) =>
  `${parseDateUtc(startDate).format('HH:mm')}\n${parseDateUtc(endDate).format('HH:mm')}`;

export const formatHours = num => {
  if (num == null) return '-';
  const hours = num / 60;
  const rhours = Math.sign(hours) === 1 ? Math.floor(hours) : Math.ceil(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);
  if (rminutes === 0) return `${rhours}h`;

  return rhours === 0 ? `${rminutes} min` : `${rhours}h  ${Math.abs(rminutes)} min`;
};

export const secondsToHms = seconds => {
  const d = Number(seconds);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor((d % 3600) % 60);

  const hDisplay = h > 0 ? h + (h === 1 ? ' h ' : ' h ') : '';
  const mDisplay = m > 0 ? m + (m === 1 ? ' mins ' : ' mins ') : '';
  const sDisplay = s > 0 ? s + (s === 1 ? ' secs' : ' secs') : '';
  return hDisplay + mDisplay + sDisplay;
};

export const twelveHourTimeFormat = dateTime => parseDateUtc(dateTime).format('hh:mm A');

export const minutesOfDay = m => {
  return m.minutes() + m.hours() * 60;
};

export const compareOnlyTimes = (date1, date2) => {
  const date1Moment = moment(date1);
  const date2Moment = moment(date2);

  return minutesOfDay(date1Moment) > minutesOfDay(date2Moment);
};

export const isValidDate = (value, allowEmpty = false) => {
  if (value === null) {
    return allowEmpty;
  }
  return moment(value).isValid();
};

// works for this format: yyyy-mm-dd -> ex: "2021-11-27T00:00:00Z"
export const isValidDateFormat = dateNumber => {
  if (dateNumber === null || dateNumber === undefined || dateNumber === '') {
    return false;
  }
  const dateString = dateNumber.toString();
  const year = parseInt(dateString.substring(0, 4), 10);
  const month = parseInt(dateString.substring(5, 7), 10);
  const day = parseInt(dateString.substring(8, 10), 10);

  const value = moment([year, month, day]);
  return value.isValid();
};

export const extractDateOnly = date => {
  date.setHours(0, 0, 0, 0);
  return date;
};

export const isDateInPast = dateValue => {
  const dateAt = moment(dateValue);
  return dateAt.isBefore(globals.now, 'day');
};

export const isDateInPastOrToday = dateValue => {
  const dateAt = moment(dateValue);
  return dateAt.isSameOrBefore(globals.now, 'day');
};

export const isDateFromBeforeDateTo = (dateFrom, dateTo, allowEmptyDates = false) => {
  if (!dateFrom || !dateTo) {
    return allowEmptyDates;
  }
  return dateFrom <= dateTo;
};

export const isAnyDateEmpty = (dateFrom, dateTo) => !!dateFrom !== !!dateTo;

export const isValidPastOrCurrentDate = (date, allowEmptyDate = false) => {
  return isValidDate(date, allowEmptyDate) && isDateFromBeforeDateTo(date, new Date(globals.now));
};

export const isPeriodWithinLimit = (dateFrom, dateTo, maxDays) => {
  return !(moment(dateTo).diff(moment(dateFrom), 'days') > maxDays);
};

export const trimmedDateAt = dateAt => {
  return moment(dateAt)
    .startOf('day')
    .format(globals.dateFormats.localApiFormat);
};
